import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { useLocale } from 'containers/App/LocaleContext';
import { useRef, useState } from 'react';
import type { FC, MouseEvent } from 'react';
import type { InfiniteScrollItemsResult } from 'utils/react/useInfiniteScrollItems';
import {
  FacetedFilterAttributeSelect,
  type FacetedFilterAttributeValueOption,
} from './FacetedFilterAttributeSelect';

interface Props {
  attributeName: string;
  attributeValues: string[];
  onDelete: () => void;
  onChangeValue: (value: string[]) => void;
  makeLoadAttributeValues: (
    attribute: string,
  ) => (
    startIndex: number,
    filter: string,
    limit?: number,
  ) => Awaitable<InfiniteScrollItemsResult<FacetedFilterAttributeValueOption>>;
  focusOnRender: boolean;
}

const formatValues = (values: string[]): string =>
  values.length > 2 ? `${values.slice(0, 2).join(', ')}...` : values.join(', ');

export const FacetedFilterAttribute: FC<Props> = (props) => {
  const {
    generic: {
      components: { facetedFilter: facetedFilterMsg },
    },
  } = useLocale();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(props.focusOnRender);

  const wrapperRef = useClickAway<HTMLDivElement>(() => {
    setOpen(false);
  });

  const handleBlur = () => {
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        setOpen(false);
      }
    }, 0);
  };

  return (
    <div
      className='faceted-filter__attribute'
      ref={wrapperRef}
      tabIndex={-1}
      onBlur={handleBlur}
      onClick={(e: MouseEvent<HTMLDivElement>) => {
        if (open) {
          e.currentTarget.blur();
        } else {
          setOpen(true);
        }
      }}
    >
      <div className='mr-2 font-semibold'>{`${props.attributeName}:`}</div>
      <Tooltip content={props.attributeValues.join(', ')}>
        <button
          className='faceted-filter__control'
          type='button'
          onClick={(e: MouseEvent<HTMLButtonElement>) => {
            if (open) {
              e.currentTarget.blur();
            }
          }}
          ref={buttonRef}
          autoFocus={props.focusOnRender}
        >
          {props.attributeValues.length > 0
            ? formatValues(props.attributeValues)
            : facetedFilterMsg.select}
          <Icon
            className='ml-1'
            glyph={open ? 'fa-angle-up' : 'fa-angle-down'}
            size='sm'
          />
        </button>
      </Tooltip>
      <button
        className='faceted-filter__btn-remove'
        type='button'
        onClick={props.onDelete}
        tabIndex={-1}
      >
        <span className='fa-solid fa-times-circle' />
      </button>
      {open && (
        <FacetedFilterAttributeSelect
          value={props.attributeValues}
          onChange={props.onChangeValue}
          wrapperRef={wrapperRef}
          loadValues={props.makeLoadAttributeValues(props.attributeName)}
          attributeName={props.attributeName}
        />
      )}
    </div>
  );
};
